import * as React from 'react';
import Box from '@mui/joy/Box';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Link from '@mui/joy/Link';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { visuallyHidden } from '@mui/utils';
import { IOffer } from '../interface/IOffer';

import Divider from '@mui/joy/Divider';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Dropdown from '@mui/joy/Dropdown';
import { CircularProgress } from '@mui/joy';
import { deutschNumberFormatter } from '../helper/utils';
import OfferPdf from './OfferPdf';
import offerApi from '../api/offer';
import { pdf } from '@react-pdf/renderer';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';

function RowMenu({ onEdit, onOrdered, onDelete, onDuplicate, onStatusChange }: any) {
  return (
    <Dropdown>
      <MenuButton slots={{ root: IconButton }} variant='plain' slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm' } }}>
        <MoreHorizRoundedIcon />
      </MenuButton>
      <Menu size='sm' sx={{ minWidth: 140 }}>
        <MenuItem onClick={onEdit}>Edit</MenuItem>
        <MenuItem onClick={onDuplicate}>Duplicate</MenuItem>
        <MenuItem onClick={onOrdered}>Ordered</MenuItem>
        <MenuItem onClick={() => onStatusChange('finished')}>Finished</MenuItem>
        <MenuItem onClick={() => onStatusChange('cancelled')}>Cancelled</MenuItem>
        <Divider />
        <MenuItem onClick={onDelete} color='danger'>
          Delete
        </MenuItem>
      </Menu>
    </Dropdown>
  );
}

const PdfDownloadButton = ({ offerId }: { offerId: string }) => {
  const handleDownload = async () => {
    const res = await offerApi.getOffer(offerId);
    let offerPositions = [...res.data.offerPositions.sort((a: any, b: any) => a.position - b.position)];
    const pdfBlob = await pdf(<OfferPdf offer={res.data} offerPositions={offerPositions} client={res.data.client!} />).toBlob();
    const url = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${res.data.offerId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return <Link onClick={handleDownload}>Pdf</Link>;
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number }) {
  return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'offerId',
    numeric: false,
    disablePadding: true,
    label: 'ID',
  },
  {
    id: 'title',
    numeric: false,
    disablePadding: false,
    label: 'Title',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'invoices',
    numeric: true,
    disablePadding: false,
    label: 'Invoices',
  },
  {
    id: 'offerValue',
    numeric: true,
    disablePadding: false,
    label: 'Offer Value',
  },
  {
    id: 'openBudget',
    numeric: true,
    disablePadding: false,
    label: 'Open Budget',
  },
  {
    id: 'action',
    numeric: true,
    disablePadding: false,
    label: 'Actions',
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <thead>
      <tr>
        {headCells.map(headCell => {
          const active = orderBy === headCell.id;
          return (
            <th key={headCell.id} aria-sort={active ? ({ asc: 'ascending', desc: 'descending' } as const)[order] : undefined}>
              <Link
                underline='none'
                color='neutral'
                textColor={active ? 'primary.plainColor' : undefined}
                component='button'
                onClick={createSortHandler(headCell.id)}
                startDecorator={headCell.numeric ? <ArrowDownwardIcon sx={[active ? { opacity: 1 } : { opacity: 0 }]} /> : null}
                endDecorator={!headCell.numeric ? <ArrowDownwardIcon sx={[active ? { opacity: 1 } : { opacity: 0 }]} /> : null}
                sx={{
                  fontWeight: 'lg',

                  '& svg': {
                    transition: '0.2s',
                    transform: active && order === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)',
                  },

                  '&:hover': { '& svg': { opacity: 1 } },
                }}
              >
                {headCell.label}
                {active ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </Link>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}
export default function OfferTable({
  order,
  setOrder,
  orderBy,
  setOrderBy,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  offers,
  totalOffer,
  loading,
  onEdit,
  onDuplicate,
  onDelete,
  onOrdered,
  onStatusChange,
}: {
  order: any;
  setOrder: any;
  orderBy: any;
  setOrderBy: any;
  page: any;
  setPage: any;
  rowsPerPage: number;
  setRowsPerPage: any;
  offers: IOffer[];
  totalOffer: number;
  loading: boolean;
  onEdit: (offerId: string) => void;
  onDuplicate: (offerId: string) => void;
  onDelete: (offerId: string) => void;
  onOrdered: (offer: IOffer) => void;
  onStatusChange: (offerId: string, status: string) => void;
}) {
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: any, newValue: number | null) => {
    setRowsPerPage(parseInt(newValue!.toString(), 10));
    setPage(0);
  };
  const getLabelDisplayedRowsTo = () => {
    if (totalOffer === -1) {
      return (page + 1) * rowsPerPage;
    }
    return rowsPerPage === -1 ? totalOffer : Math.min(totalOffer, (page + 1) * rowsPerPage);
  };
  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalOffer) : 0;
  return (
    <Sheet variant='outlined' sx={{ width: '100%', boxShadow: 'sm', borderRadius: 'sm' }}>
      <Table
        aria-labelledby='tableTitle'
        hoverRow
        sx={{
          '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
          '--Table-headerUnderlineThickness': '1px',
          '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
          '--TableCell-paddingY': '4px',
          '--TableCell-paddingX': '8px',
          '& thead th:nth-child(1)': {
            width: '80px',
          },
          '& thead th:nth-child(2)': {
            width: '40%',
          },
          '& tr > *:nth-child(n+4)': { textAlign: 'right' },
        }}
      >
        <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} rowCount={totalOffer} />
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                  }}
                >
                  <CircularProgress size='lg' />
                </Box>
              </td>
            </tr>
          ) : (
            [...offers]
              // .sort(getComparator(order, orderBy))
              .map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <tr tabIndex={-1} key={row.offerId}>
                    <td id={labelId}>
                      <Typography level='body-xs'>{row.offerId}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.title}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.status}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.invoiceCount}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{deutschNumberFormatter(row.offerValue)} €</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{deutschNumberFormatter(row.openBudget)} €</Typography>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <PdfDownloadButton offerId={row.offerId} />
                        <RowMenu
                          onEdit={() => onEdit(row.offerId)}
                          onDelete={() => onDelete(row.offerId)}
                          onOrdered={() => onOrdered(row)}
                          onDuplicate={() => onDuplicate(row.offerId)}
                          onStatusChange={(newStatus: string) => onStatusChange(row.offerId, newStatus)}
                        />
                      </Box>
                    </td>
                  </tr>
                );
              })
          )}
          {emptyRows > 0 && (
            <tr
              style={
                {
                  height: `calc(${emptyRows} * 40px)`,
                  '--TableRow-hoverBackground': 'transparent',
                } as React.CSSProperties
              }
            >
              <td colSpan={6} aria-hidden />
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={7}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'flex-end',
                }}
              >
                <FormControl orientation='horizontal' size='sm'>
                  <FormLabel>Rows per page:</FormLabel>
                  <Select onChange={handleChangeRowsPerPage} value={rowsPerPage}>
                    <Option value={5}>5</Option>
                    <Option value={10}>10</Option>
                    <Option value={25}>25</Option>
                  </Select>
                </FormControl>
                <Typography sx={{ textAlign: 'center', minWidth: 80 }}>
                  {labelDisplayedRows({
                    from: totalOffer === 0 ? 0 : page * rowsPerPage + 1,
                    to: getLabelDisplayedRowsTo(),
                    count: totalOffer === -1 ? -1 : totalOffer,
                  })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size='sm'
                    color='neutral'
                    variant='outlined'
                    disabled={page === 0}
                    onClick={() => handleChangePage(page - 1)}
                    sx={{ bgcolor: 'background.surface' }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <IconButton
                    size='sm'
                    color='neutral'
                    variant='outlined'
                    disabled={totalOffer !== -1 ? page >= Math.ceil(totalOffer / rowsPerPage) - 1 : false}
                    onClick={() => handleChangePage(page + 1)}
                    sx={{ bgcolor: 'background.surface' }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Box>
              </Box>
            </td>
          </tr>
        </tfoot>
      </Table>
    </Sheet>
  );
}
